import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase';

const WorkspaceContext = createContext();

export const WorkspaceProvider = ({ authUserId, children }) => {
  const [profileUserId, setProfileUserId] = useState(null);
  const [systemRole, setSystemRole] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceNames, setWorkspaceNames] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState("Select a Workspace");
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);  
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!authUserId) return;

    const fetchUserData = async () => {
      try {
        console.log("Fetching user data for authUserId:", authUserId);

        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('id, system_role')
          .eq('id', authUserId)
          .maybeSingle();
        if (profileError || !profileData) throw new Error(profileError?.message || "User profile not found");

        setProfileUserId(profileData.id);
        setSystemRole(profileData.system_role);

        let fetchedWorkspaces = [];

        if (profileData.system_role?.startsWith("org_")) {
          const { data: allWorkspaces, error: allWorkspacesError } = await supabase
            .from('clients')
            .select('id, name');
          if (allWorkspacesError) throw new Error(allWorkspacesError.message);
          fetchedWorkspaces = allWorkspaces || [];
        } else {
          const { data: workspaceIds, error: workspaceIdError } = await supabase
            .from('user_workspace_roles')
            .select('workspace_id')
            .eq('user_id', authUserId);
          if (workspaceIdError) throw new Error(workspaceIdError.message);

          if (workspaceIds?.length > 0) {
            const workspaceIdList = workspaceIds.map(w => w.workspace_id);
            const { data: workspaceNamesData, error: workspaceNameError } = await supabase
              .from('clients')
              .select('id, name')
              .in('id', workspaceIdList);
            if (workspaceNameError) throw new Error(workspaceNameError.message);
            fetchedWorkspaces = workspaceNamesData || [];
          }
        }

        setWorkspaces(fetchedWorkspaces);
        setWorkspaceNames(fetchedWorkspaces.map(w => w.name));

        if (fetchedWorkspaces.length > 0) {
          setSelectedWorkspace(fetchedWorkspaces[0].name);
          setSelectedWorkspaceId(fetchedWorkspaces[0].id);  
        }

      } catch (error) {
        console.error("Error fetching user data:", error.message);
        setErrorMessage(error.message);
      }
    };

    fetchUserData();
  }, [authUserId]);

  useEffect(() => {
    const selected = workspaces.find(w => w.name === selectedWorkspace);
    if (selected) {
      console.log("Setting selectedWorkspaceId:", selected.id);
      setSelectedWorkspaceId(selected.id);
    }
  }, [selectedWorkspace, workspaces]);

  return (
    <WorkspaceContext.Provider value={{
      authUserId,
      profileUserId,
      systemRole,
      workspaces,
      workspaceNames,
      selectedWorkspace,
      setSelectedWorkspace,
      selectedWorkspaceId,  
      setSelectedWorkspaceId, 
      errorMessage
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => useContext(WorkspaceContext);
